import { useState, useEffect, useCallback } from 'react';
import { NAVY, NAVY_LIGHT, GOLD, GOLD_LIGHT, BG, WHITE } from './lib/ui';
import { Spinner } from './lib/ui';
import { supabase } from './lib/supabase';
import * as db from './lib/supabase';
import { createNotification } from './lib/supabase';

import LoginScreen    from './components/LoginScreen';
import ClientPortal   from './components/ClientPortal';
import Dashboard      from './components/Dashboard';
import MattersList    from './components/MattersList';
import MatterDetail   from './components/MatterDetail';
import TaskDetail     from './components/TaskDetail';
import AllTasksView   from './components/AllTasksView';
import MyTasksView    from './components/MyTasksView';
import TeamView       from './components/TeamView';
import ClientsView    from './components/ClientsView';
import CalendarView   from './components/CalendarView';
import MessagingView  from './components/MessagingView';
import ArchiveView    from './components/ArchiveView';
import TemplateLibrary  from './components/TemplateLibrary';
import NotificationBell from './components/NotificationBell';
import { AddMatterModal, AddTaskModal, AddNoteModal } from './components/Modals';

const ADMIN_EMAIL = 'emrekoluman@koluman.av.tr';
const DONE = ['Completed', 'Closed'];

const NAV_ITEMS = [
  { k:'dashboard', label:'Home',     icon:'⬛' },
  { k:'mytasks',   label:'My Tasks', icon:'✅' },
  { k:'matters',   label:'Matters',  icon:'📁' },
  { k:'calendar',  label:'Calendar', icon:'📅' },
  { k:'messages',  label:'Messages', icon:'💬' },
];

const isPushSupported = () =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

const subscribeToPush = async (memberId) => {
  try {
    if (!isPushSupported()) return false;
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return false;
    const reg = await navigator.serviceWorker.ready;
    const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!vapidKey) return false;
    const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
    const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const key = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) key[i] = raw.charCodeAt(i);
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
    const json = sub.toJSON();
    await supabase.from('push_subscriptions').upsert([{
      member_id: memberId, endpoint: json.endpoint,
      p256dh: json.keys.p256dh, auth: json.keys.auth,
    }], { onConflict: 'endpoint' });
    return true;
  } catch(e) { console.log('Push error:', e); return false; }
};

const sendPush = async (memberId, payload) => {
  try {
    await fetch('/api/push', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ memberId, payload }),
    });
  } catch(e) {}
};

export default function App() {
  const [session,        setSession]        = useState(undefined);
  const [userType,       setUserType]       = useState(null);
  const [matters,        setMatters]        = useState([]);
  const [team,           setTeam]           = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [view,           setView]           = useState('dashboard');
  const [activeMatter,   setActiveMatter]   = useState(null);
  const [activeTask,     setActiveTask]     = useState(null);
  const [modal,          setModal]          = useState(null);
  const [sidebarOpen,    setSidebarOpen]    = useState(false);
  const [filterPreset,   setFilterPreset]   = useState(null);
  const [notifDismissed, setNotifDismissed] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'TOKEN_REFRESHED') { setSession(s); return; }
      setSession(s);
      if (!s) { setUserType(null); setMatters([]); setTeam([]); }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) return;
    supabase.from('team').select('id').eq('email', session.user?.email).single()
      .then(({ data }) => setUserType(data ? 'team' : 'client'));
  }, [session]);

  const loadAll = useCallback(async () => {
    try {
      const [m, t] = await Promise.all([db.getMatters(), db.getTeam()]);
      setMatters(m); setTeam(t);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (session && userType === 'team') { setLoading(true); loadAll(); }
    if (session && userType === 'client') setLoading(false);
  }, [session, userType, loadAll]);

  useEffect(() => {
    if (userType !== 'team' || !team.length || !session) return;
    const me = team.find(m => m.email === session.user?.email);
    if (me?.id && isPushSupported() && Notification.permission === 'granted') {
      subscribeToPush(me.id);
    }
  }, [userType, team, session]);

  if (session === undefined || (session && userType === null)) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'100dvh', background:NAVY }}>
      <Spinner />
    </div>
  );
  if (!session) return <LoginScreen />;
  if (userType === 'client') return <ClientPortal session={session} />;

  const currentMatter     = matters.find(m => m.id === activeMatter);
  const currentTask       = currentMatter?.tasks?.find(t => t.id === activeTask);
  const allTasks          = matters.flatMap(m => (m.tasks||[]).map(t => ({ ...t, matterName: m.name, matter_id: m.id })));
  const activeMatters     = matters.filter(m => !DONE.includes(m.status));
  const activeTasks       = allTasks.filter(t => !DONE.includes(t.status));
  const currentUserEmail  = session.user?.email || '';
  const currentTeamMember = team.find(m => m.email === currentUserEmail);
  const displayName       = currentTeamMember?.name || currentUserEmail;
  const isAdmin           = currentUserEmail === ADMIN_EMAIL;
  const notifStatus       = isPushSupported() ? Notification.permission : 'unsupported';

  const handleEnableNotifications = async () => {
    if (!currentTeamMember) return;
    const ok = await subscribeToPush(currentTeamMember.id);
    setNotifDismissed(true);
    if (ok) alert('Notifications enabled!');
  };

  const handleAddMatter = async (data) => {
    const m = await db.addMatter(data);
    setMatters(ms => [{ ...m, tasks:[], matter_notes:[] }, ...ms]);
    setModal(null);
  };

  const handleUpdateMatter = async (patch) => {
    const u = await db.updateMatter(activeMatter, patch);
    setMatters(ms => ms.map(m => m.id === activeMatter ? { ...m, ...u } : m));
  };

  const handleDeleteMatter = async () => {
    await db.deleteMatter(activeMatter);
    setMatters(ms => ms.filter(m => m.id !== activeMatter));
    setActiveMatter(null); setView('matters');
  };

  const handleAddTask = async (data) => {
    const t = await db.addTask({ ...data, matter_id: activeMatter });
    setMatters(ms => ms.map(m => m.id === activeMatter
      ? { ...m, tasks: [...(m.tasks||[]), { ...t, subtasks:[], task_notes:[] }] } : m));
    setModal(null);
    const matter = matters.find(m => m.id === activeMatter);
    for (const assigneeId of (data.assignees || [])) {
      if (assigneeId === currentTeamMember?.id) continue;
      const payload = { title: `New task: ${data.title}`, body: matter?.name || 'Koluman Law', view: 'mytasks' };
      await createNotification({ recipient_id: assigneeId, type: 'task_assigned', title: payload.title, body: payload.body, link_view: 'mytasks', link_id: t.id });
      await sendPush(assigneeId, payload);
    }
  };

  const handleUpdateTask = async (tid, patch) => {
    const u = await db.updateTask(tid, patch);
    setMatters(ms => ms.map(m => ({ ...m, tasks: (m.tasks||[]).map(t => t.id === tid ? { ...t, ...u } : t) })));
  };

  const handleDeleteTask = async (tid) => {
    await db.deleteTask(tid);
    setMatters(ms => ms.map(m => m.id === activeMatter
      ? { ...m, tasks: (m.tasks||[]).filter(t => t.id !== tid) } : m));
    setActiveTask(null);
  };

  const handleAddSubtask = async (tid, data) => {
    const s = await db.addSubtask({ ...data, task_id: tid });
    setMatters(ms => ms.map(m => m.id === activeMatter ? {
      ...m, tasks: m.tasks.map(t => t.id === tid ? { ...t, subtasks: [...(t.subtasks||[]), s] } : t)
    } : m));
  };

  const handleUpdateSubtask = async (tid, sid, patch) => {
    const u = await db.updateSubtask(sid, patch);
    setMatters(ms => ms.map(m => m.id === activeMatter ? {
      ...m, tasks: m.tasks.map(t => t.id === tid ? {
        ...t, subtasks: (t.subtasks||[]).map(s => s.id === sid ? { ...s, ...u } : s)
      } : t)
    } : m));
  };

  const handleDeleteSubtask = async (tid, sid) => {
    await db.deleteSubtask(sid);
    setMatters(ms => ms.map(m => m.id === activeMatter ? {
      ...m, tasks: m.tasks.map(t => t.id === tid ? {
        ...t, subtasks: (t.subtasks||[]).filter(s => s.id !== sid)
      } : t)
    } : m));
  };

  const handleAddMatterNote = async (data) => {
    const n = await db.addMatterNote({ ...data, matter_id: activeMatter });
    setMatters(ms => ms.map(m => m.id === activeMatter
      ? { ...m, matter_notes: [...(m.matter_notes||[]), n] } : m));
    setModal(null);
  };

  const handleAddTaskNote = async (tid, data) => {
    const n = await db.addTaskNote({ ...data, task_id: tid });
    setMatters(ms => ms.map(m => m.id === activeMatter ? {
      ...m, tasks: m.tasks.map(t => t.id === tid
        ? { ...t, task_notes: [...(t.task_notes||[]), n] } : t)
    } : m));
    const task = matters.flatMap(m => m.tasks||[]).find(t => t.id === tid);
    if (task) {
      for (const assigneeId of (task.assignees || [])) {
        if (assigneeId === currentTeamMember?.id) continue;
        const payload = { title: `New note on: ${task.title}`, body: data.text?.slice(0,80), view: 'mytasks' };
        await createNotification({ recipient_id: assigneeId, type: 'task_note', title: payload.title, body: payload.body, link_view: 'mytasks', link_id: tid });
        await sendPush(assigneeId, payload);
      }
    }
  };

  const handleDeleteTaskNote = async (tid, nid) => {
    await db.deleteTaskNote(nid);
    setMatters(ms => ms.map(m => m.id === activeMatter ? {
      ...m, tasks: m.tasks.map(t => t.id === tid
        ? { ...t, task_notes: (t.task_notes||[]).filter(n => n.id !== nid) } : t)
    } : m));
  };

  const handleAddTeam    = async (data) => { const m = await db.addTeamMember(data); setTeam(t => [...t, m]); return m; };
  const handleDeleteTeam = async (id)   => { await db.deleteTeamMember(id); setTeam(t => t.filter(m => m.id !== id)); };
  const handleSignOut    = async ()     => { await supabase.auth.signOut(); };
  const handleMatterClientUpdate = (mid, patch) => {
    setMatters(ms => ms.map(m => m.id === mid ? { ...m, ...patch } : m));
  };

  const navigateTo = (k) => { setView(k); setActiveMatter(null); setActiveTask(null); setSidebarOpen(false); };

  const overdueCount = activeTasks.filter(t =>
    t.deadline && new Date(t.deadline) < new Date() && !DONE.includes(t.status)
  ).length;

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'100dvh', background:BG, flexDirection:'column', gap:16 }}>
      <div style={{ fontFamily:'Georgia,serif', fontSize:24, fontWeight:700, color:NAVY }}>Koluman<span style={{ color:GOLD }}>Law</span></div>
      <Spinner />
    </div>
  );

  if (error) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'100dvh', background:BG, flexDirection:'column', gap:12, padding:32 }}>
      <div style={{ fontFamily:'Georgia,serif', fontSize:22, fontWeight:700, color:NAVY }}>Connection Error</div>
      <div style={{ fontSize:13, color:'#C0392B', maxWidth:480, textAlign:'center' }}>{error}</div>
      <button onClick={loadAll} style={{ marginTop:8, background:NAVY, color:GOLD, border:'none', borderRadius:7, padding:'10px 24px', cursor:'pointer', fontSize:13, fontWeight:700, fontFamily:'inherit' }}>Retry</button>
    </div>
  );

  const pageTitle = activeMatter && currentMatter ? currentMatter.name
    : view==='dashboard' ? 'Dashboard'
    : view==='matters'   ? 'All Matters'
    : view==='tasks'     ? 'All Tasks'
    : view==='mytasks'   ? 'My Tasks'
    : view==='calendar'  ? 'Calendar'
    : view==='messages'  ? 'Messages'
    : view==='archive'   ? 'Archive'
    : view==='templates' ? 'Template Library'
    : view==='clients'   ? 'Client Accounts'
    : 'Team';

  const DesktopNavItem = ({ k, label, icon }) => (
    <button onClick={() => navigateTo(k)} style={{
      display:'flex', alignItems:'center', gap:10, width:'100%', padding:'10px 20px',
      background: view===k && !activeMatter ? GOLD+'20' : 'none',
      borderLeft: view===k && !activeMatter ? '3px solid '+GOLD : '3px solid transparent',
      color: view===k && !activeMatter ? GOLD : '#8C9DBB',
      border:'none', cursor:'pointer', fontSize:13, fontFamily:'inherit',
      fontWeight: view===k && !activeMatter ? 700 : 400, textAlign:'left'
    }}>
      <span style={{ fontSize:14 }}>{icon}</span>{label}
    </button>
  );

  const SidebarContent = (
    <>
      <div style={{ padding:'22px 20px 16px', borderBottom:'1px solid '+NAVY_LIGHT }}>
        <div style={{ fontFamily:'Georgia,serif', fontSize:21, fontWeight:700, color:WHITE }}>Koluman<span style={{ color:GOLD }}>Law</span></div>
        <div style={{ fontSize:10, color:GOLD_LIGHT, letterSpacing:2, marginTop:3, textTransform:'uppercase' }}>Internal Platform</div>
      </div>
      <nav style={{ flex:1, paddingTop:12, overflowY:'auto' }}>
        <DesktopNavItem k="dashboard" label="Dashboard"       icon="&#9632;" />
        <DesktopNavItem k="matters"   label="Matters"         icon="&#128193;" />
        <DesktopNavItem k="tasks"     label="All Tasks"       icon="&#9989;" />
        <DesktopNavItem k="mytasks"   label="My Tasks"        icon="&#128100;" />
        <DesktopNavItem k="calendar"  label="Calendar"        icon="&#128197;" />
        <DesktopNavItem k="messages"  label="Messages"        icon="&#128172;" />
        <div style={{ margin:'10px 16px', borderTop:'1px solid #2D3F66' }} />
        <DesktopNavItem k="archive"   label="Archive"         icon="&#128452;" />
        <DesktopNavItem k="templates" label="Templates"       icon="&#128218;" />
        <DesktopNavItem k="clients"   label="Client Accounts" icon="&#128101;" />
        <DesktopNavItem k="team"      label="Team"            icon="&#128101;" />
        {overdueCount > 0 && (
          <div style={{ margin:'12px 16px', background:'#C0392B22', border:'1px solid #C0392B44', borderRadius:7, padding:'8px 12px' }}>
            <div style={{ fontSize:11, color:'#FF8A80', fontWeight:700 }}>&#9888; {overdueCount} overdue</div>
          </div>
        )}
      </nav>
      <div style={{ padding:'12px 16px', borderTop:'1px solid '+NAVY_LIGHT }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
          <div style={{ width:28, height:28, borderRadius:'50%', background:GOLD+'33', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:GOLD }}>
            {displayName.slice(0,2).toUpperCase()}
          </div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:12, fontWeight:600, color:WHITE, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{displayName}</div>
            <div style={{ fontSize:10, color:'#6A7A9A', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{currentUserEmail}</div>
          </div>
        </div>
        <button onClick={() => { setModal('addMatter'); setSidebarOpen(false); }} style={{ width:'100%', background:GOLD, color:NAVY, border:'none', borderRadius:7, padding:'9px 0', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit', marginBottom:8 }}>+ New Matter</button>
        {isPushSupported() && notifStatus !== 'granted' && (
          <button onClick={handleEnableNotifications} style={{ width:'100%', background:'none', color:GOLD, border:`1px solid ${GOLD}44`, borderRadius:7, padding:'7px 0', fontSize:11, cursor:'pointer', fontFamily:'inherit', marginBottom:8 }}>
            &#128276; Enable Notifications
          </button>
        )}
        <button onClick={handleSignOut} style={{ width:'100%', background:'none', color:'#6A7A9A', border:'1px solid #2D3F66', borderRadius:7, padding:'7px 0', fontSize:11, cursor:'pointer', fontFamily:'inherit' }}>Sign Out</button>
      </div>
    </>
  );

  return (
    <div className="app-root" style={{ fontFamily:"'Segoe UI', system-ui, sans-serif", background:BG, color:'#1A1A1A', position:'relative' }}>
      <div className="sidebar-desktop" style={{ width:220, background:NAVY }}>
        {SidebarContent}
      </div>
      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:200 }} />
      )}
      <div className="sidebar-mobile" style={{ transform: sidebarOpen ? 'translateX(0)' : 'translateX(-220px)', transition:'transform 0.25s ease' }}>
        {SidebarContent}
      </div>
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden', minWidth:0 }}>
        <div style={{ background:WHITE, borderBottom:'1px solid #E5E0D8', padding:'0 16px', display:'flex', alignItems:'center', gap:8, height:52, flexShrink:0 }}>
          <button className="mobile-topbar-menu" onClick={() => setSidebarOpen(s => !s)}>&#9776;</button>
          <span style={{ color:NAVY, fontFamily:'Georgia,serif', fontSize:15, fontWeight:700, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{pageTitle}</span>
          <NotificationBell recipientId={currentTeamMember?.id} onNavigate={(v) => { setView(v); setActiveMatter(null); setActiveTask(null); }} />
          <div style={{ display:'flex', gap:8, alignItems:'center', flexShrink:0 }}>
            {activeMatter && !activeTask && (
              <>
                <button onClick={() => { setActiveMatter(null); setActiveTask(null); setView('matters'); }} style={{ background:'none', border:'1px solid #D0CBBF', borderRadius:6, padding:'5px 10px', fontSize:11, cursor:'pointer', color:NAVY, fontFamily:'inherit' }}>&#8592; Back</button>
                <button onClick={() => setModal('addTask')} style={{ background:NAVY, color:GOLD, border:'none', borderRadius:6, padding:'6px 10px', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>+ Task</button>
                <button onClick={() => setModal('addNote')} style={{ background:GOLD, color:NAVY, border:'none', borderRadius:6, padding:'6px 10px', fontSize:11, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>+ Note</button>
              </>
            )}
            {activeTask && (
              <button onClick={() => setActiveTask(null)} style={{ background:'none', border:'1px solid #D0CBBF', borderRadius:6, padding:'5px 10px', fontSize:11, cursor:'pointer', color:NAVY, fontFamily:'inherit' }}>&#8592; Back</button>
            )}
          </div>
        </div>
        <div className="main-content">
          {/* Notification banner */}
          {!notifDismissed && notifStatus === 'default' && isPushSupported() && (
            <div style={{ background:NAVY, borderRadius:10, padding:'14px 18px', marginBottom:16, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
              <span style={{ fontSize:22 }}>&#128276;</span>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:700, color:GOLD }}>Enable Notifications</div>
                <div style={{ fontSize:12, color:'#8AACCC', marginTop:2 }}>Get alerts for new tasks and messages</div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={handleEnableNotifications} style={{ background:GOLD, color:NAVY, border:'none', borderRadius:7, padding:'8px 16px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>Enable</button>
                <button onClick={() => setNotifDismissed(true)} style={{ background:'none', color:'#6A7A9A', border:'1px solid #2D3F66', borderRadius:7, padding:'8px 12px', fontSize:12, cursor:'pointer', fontFamily:'inherit' }}>Not now</button>
              </div>
            </div>
          )}
          {view==='dashboard' && !activeMatter && <Dashboard matters={activeMatters} team={team} allTasks={activeTasks} setView={setView} setActiveMatter={setActiveMatter} setFilterPreset={setFilterPreset} />}
          {view==='matters'   && !activeMatter && <MattersList matters={activeMatters} setActiveMatter={setActiveMatter} setView={setView} filterPreset={filterPreset} />}
          {view==='tasks'     && !activeMatter && <AllTasksView tasks={activeTasks} team={team} setActiveMatter={(mid) => { setActiveMatter(mid); setView('matters'); }} setActiveTask={setActiveTask} filterPreset={filterPreset} />}
          {view==='mytasks'   && !activeMatter && <MyTasksView tasks={activeTasks} team={team} currentUserEmail={currentUserEmail} setActiveMatter={(mid) => { setActiveMatter(mid); setView('matters'); }} setActiveTask={setActiveTask} />}
          {view==='calendar'  && !activeMatter && <CalendarView team={team} matters={matters} currentUserEmail={currentUserEmail} />}
          {view==='messages'  && !activeMatter && <MessagingView team={team} matters={matters} currentUserEmail={currentUserEmail} />}
          {view==='archive'   && !activeMatter && <ArchiveView matters={matters} allTasks={allTasks} team={team} setActiveMatter={setActiveMatter} setActiveTask={setActiveTask} setView={setView} />}
          {view==='templates' && !activeMatter && <TemplateLibrary currentUserEmail={currentUserEmail} />}
          {view==='clients'   && !activeMatter && <ClientsView matters={matters} onMatterUpdate={handleMatterClientUpdate} isAdmin={isAdmin} />}
          {view==='team'      && !activeMatter && <TeamView team={team} onAdd={handleAddTeam} onDelete={handleDeleteTeam} allTasks={allTasks} currentUserEmail={currentUserEmail} isAdmin={isAdmin} />}
          {activeMatter && currentMatter && !activeTask && (
            <MatterDetail matter={currentMatter} team={team}
              updateMatter={handleUpdateMatter} deleteMatter={handleDeleteMatter}
              updateTask={(tid,p) => handleUpdateTask(tid,p)}
              deleteTask={(tid) => handleDeleteTask(tid)}
              setActiveTask={setActiveTask} setModal={setModal} />
          )}
          {activeTask && currentTask && currentMatter && (
            <TaskDetail task={currentTask} team={team}
              updateTask={(p) => handleUpdateTask(currentTask.id,p)}
              addSubtask={(d) => handleAddSubtask(currentTask.id,d)}
              updateSubtask={(sid,p) => handleUpdateSubtask(currentTask.id,sid,p)}
              deleteSubtask={(sid) => handleDeleteSubtask(currentTask.id,sid)}
              addTaskNote={(d) => handleAddTaskNote(currentTask.id,d)}
              deleteTaskNote={(nid) => handleDeleteTaskNote(currentTask.id,nid)}
              deleteTask={() => handleDeleteTask(currentTask.id)}
              onBack={() => setActiveTask(null)} />
          )}
        </div>
      </div>
      <nav className="mobile-nav">
        <div className="mobile-nav-inner">
          {NAV_ITEMS.map(item => (
            <button key={item.k} className={`mobile-nav-btn${view===item.k && !activeMatter ? ' active' : ''}`} onClick={() => navigateTo(item.k)}>
              <span className="icon">{item.icon}</span>{item.label}
            </button>
          ))}
          <button className={`mobile-nav-btn${sidebarOpen ? ' active' : ''}`} onClick={() => setSidebarOpen(s => !s)}>
            <span className="icon">&#9776;</span>More
          </button>
        </div>
      </nav>
      {modal==='addMatter' && <AddMatterModal team={team} onClose={() => setModal(null)} onSave={handleAddMatter} />}
      {modal==='addTask' && currentMatter && <AddTaskModal team={team} onClose={() => setModal(null)} onSave={handleAddTask} />}
      {modal==='addNote' && currentMatter && <AddNoteModal team={team} onClose={() => setModal(null)} onSave={handleAddMatterNote} />}
    </div>
  );
}
